<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import { TeleportationTarget } from '~/models/TeleportationTarget'
import { KsButton, KsDrawer, KsInput } from '@aschehoug/kloss'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import useSubjectsStore from '~/stores/subjects'
import { gradesSorted } from '~/utils/gradeSorter'
import useGroupsStore from '~/stores/groups'
import { findSubjectsByGrade } from '~/utils/subjectMetadata'
import { isExcludedSubject, sortBySubjectIndex } from '~/utils/subjectSorter'
import arrayUtils from '~/utils/arrayUtils'
import SettingsCustomizationCallout from '~/components/settings/SettingsCustomizationCallout.vue'

const props = defineProps<{
  open: boolean
}>()

const { unique } = arrayUtils()
const { t } = useI18n()
const authStore = useAuthStore()
const { userSubjectsByGrades, userRelevantGrades, userGrades, activeUserGroupGrades } = storeToRefs(authStore)

const {
  toggleGrade,
  hasGrade,
  isReadOnlyGrade,
  toggleSubject,
  hasSubject,
} = authStore

const { subjects } = storeToRefs(useSubjectsStore())
const groupsStore = useGroupsStore()
const { selectedGroup } = storeToRefs(groupsStore)
const ksToast = <Toast>inject('ksToast')

const userSubjectsByRelevantGrades = computed((): SubjectCode[] => {
  return userRelevantGrades.value
    .flatMap(findSubjectsByGrade)
    .filter(unique)
    .filter((subjectCode) => !isExcludedSubject(subjectCode))
    .sort(sortBySubjectIndex)
})

const relevantSubjects = computed((): SubjectCode[] => {
  if (!hasGroupWithGrade.value) return userGrades.value.length
  ? userSubjectsByGrades.value
  : subjects.value
  return userSubjectsByRelevantGrades.value
})

const hasGroupWithGrade = computed(() => {
  return (activeUserGroupGrades.value || []).length
})

const goBack = () => {
  if (userGrades.value.length) {
    emit('back')
  }
  else ksToast.info(t('settings.chooseGradeToClose'))
}

const emit = defineEmits(['back', 'close'])
</script>

<template>
  <Teleport :to="TeleportationTarget.AppTop">
    <KsDrawer
      :title="t('settings.drawers.customization')"
      :open="props.open"
      :backdrop="false"
      size="600px"
      @close="emit('close')"
    >
      <template #header>
        <KsButton
          shape="round"
          iconLeft="arrow-left"
          :aria-label="t('back')"
          :title="t('back')"
          @click="goBack"
        />
      </template>
      <template #body>
        <div
          v-if="selectedGroup"
          class="border-t border-gray-10 py-6"
        >
          <SettingsCustomizationCallout />
        </div>
        <form class="flex flex-col gap-6 border-t border-gray-10 py-6">
          <fieldset>
            <legend
              class="mb-2 text-base font-medium uppercase tracking-wide text-gray-40"
              v-text="t('filters.chooseGrade')"
            />
            <div class="flex flex-wrap gap-4">
              <KsButton
                v-for="grade in gradesSorted"
                :key="grade"
                variant="secondary"
                shape="rounded"
                type="label"
              >
                <KsInput
                  v-if="hasGroupWithGrade"
                  type="checkbox"
                  :checked="selectedGroup?.grade === grade"
                  disabled
                />
                <KsInput
                  v-else
                  type="checkbox"
                  :checked="hasGrade(grade)"
                  :disabled="isReadOnlyGrade(grade) && hasGrade(grade)"
                  @click="toggleGrade(grade)"
                />
                {{ t(`metadata.grades.${grade}`) }}
              </KsButton>
            </div>
          </fieldset>
          <hr
            class="border-t border-gray-10"
            aria-hidden="true"
          >
          <fieldset>
            <legend
              class="mb-2 text-base font-medium uppercase tracking-wide text-gray-40"
              v-text="t('filters.chooseSubject')"
            />
            <p
              v-if="!userGrades.length"
              class="text-gray-40"
              v-text="t('settings.chooseGrade')"
            />
            <div
              v-else
              class="flex flex-wrap gap-4"
            >
              <KsButton
                v-for="subject in relevantSubjects"
                :key="subject"
                variant="secondary"
                shape="rounded"
                type="label"
              >
                <KsInput
                  type="checkbox"
                  :checked="hasSubject(subject) || selectedGroup?.subjects[0] === subject"
                  :disabled="selectedGroup?.subjects[0] === subject"
                  @click="toggleSubject(subject)"
                />
                {{ t(`metadata.subjects.${subject}`) }}
              </KsButton>
            </div>
          </fieldset>
        </form>
      </template>
    </KsDrawer>
  </Teleport>
</template>
